import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import useExtensionDisplayName from 'flow/hooks/useExtensionDisplayName';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { selectTicketActionEventActor } from 'flow/selectors/TicketEventSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { TicketActionEventKey } from 'flow/types/TicketEventData';

// Localized "Changed by …" tooltip line for a ticket action event:
// "Changed by you" when the logged-in user made the change, "Changed by
// <name>" for another agent, or undefined for a system-driven change (no
// human actor to credit).
const useActorTooltipLine = (
    ticketKey: TicketKey,
    eventKey: TicketActionEventKey,
): string | undefined => {
    const { i18next } = useAppContext();

    const actor = useAppSelector((state) =>
        selectTicketActionEventActor(state, ticketKey, eventKey),
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const actorIsExtension = !!actor && actor !== 'SYSTEM';
    const actorLabel = useExtensionDisplayName(
        actorIsExtension ? actor : undefined,
    );

    if (!actorIsExtension) {
        return undefined;
    }
    if (actor === ownExtensionKey) {
        return i18next.t('tickets.tooltip.actor.self');
    }
    return i18next.t('tickets.tooltip.actor', { value: actorLabel });
};

export default useActorTooltipLine;
